<template>
  <MainLayout>
    <ContentPageHeader functionName="店舗移動管理" pageName="店舗移動登録(入荷)">
    </ContentPageHeader>
    
    <!-- 基本セクション -->
    <div class="search-section">
      <div class="search-form">        
        <FormDisplayField 
          v-if="storeTransfer"
          label="移動元店舗"
          :value="getStoreName(storeTransfer.movingSourceStore)"
        />
        
        <FormDisplayField 
          v-if="storeTransfer"
          label="移動金額"
          :value="calculateTotalAmount"
        />
        
        <FormTextField 
          label="商品コード" 
          id="productCode" 
          v-model="productCode"
          placeholder="商品コードを入力してください"
          size="medium"
          :disabled="isDetailMode"
        />

        <FormDisplayField 
          label="ID" 
          :value="searchedProduct ? searchedProduct.id : null"
        />

        <FormDisplayField 
          label="カテゴリ" 
          :value="searchedProduct ? searchedProduct.categoryName : null"
        />

        <FormDisplayField 
          label="メーカー" 
          :value="searchedProduct ? searchedProduct.makerName : null"
        />

        <FormDisplayField 
          label="商品コード" 
          :value="searchedProduct ? searchedProduct.productCode : null"
        />
        
        <FormDisplayField 
          label="商品名" 
          :value="searchedProduct ? searchedProduct.productName : null"
        />
      </div>
    </div>

    <!-- 検索結果テーブル -->
    <div class="search-result">
      <FormListTable
        :items="displayProductList"
        :columns="columns"
        :actions="actions"
        :per-page="perPage"
        :page="currentPage"
        :totalItems="totalCount"
        :emptyMessage="'商品が登録されていません'"
        @page-change="onPageChange"
        @action="handleTableAction"
      >
        <!-- 完了ステータス表示 -->
        <template #isComplete="{ item }">
          <span v-if="item.isComplete" class="status-complete">○</span>
          <span v-else class="status-incomplete"></span>
        </template>
      </FormListTable>
    </div>

    <template #footer-buttons>
      <div class="footer-buttons">
        <button class="btn btn-primary" @click="saveArrival" :disabled="isDetailMode">登録</button>
      </div>
    </template>
  </MainLayout>
</template>

<script>
import MainLayout from '@/components/layout/MainLayout.vue'
import ContentPageHeader from '@/components/layout/ContentPageHeader.vue'
import FormTextField from '@/components/form/FormTextField.vue'
import FormListTable from '@/components/form/FormListTable.vue'
import FormDisplayField from '@/components/form/FormDisplayField.vue'
import service from '../../services/service';
import { showLoading, hideLoading } from '@/utils/loadingEventBus';
import constants from '../../constants/constants.json';

export default {
  name: 'StoreTransferArrivalView',
  components: {
    MainLayout,
    ContentPageHeader,
    FormTextField,
    FormListTable,
    FormDisplayField,
  },
  data() {
    return {
      storeTransfer: null,
      productCode: '',
      allProducts: [],
      displayProductList: [],
      isDetailMode: false,

      storeOptions: [],
      searchedProduct: null, // 検索された商品情報
      // ページング
      currentPage: 0,
      perPage: 50,
      totalCount: 0,
      // テーブルカラム定義
      columns: [
        { key: 'id', label: 'ID', sortable: false, minWidth: 80, maxWidth: 100 },
        { key: 'categoryName', label: 'カテゴリ', sortable: false, minWidth: 180, maxWidth: 400 },
        { key: 'makerName', label: 'メーカー', sortable: false, minWidth: 90, maxWidth: 450 },
        { key: 'productCode', label: '商品コード', sortable: false, minWidth: 140, maxWidth: 180 },
        { key: 'productName', label: '商品名', sortable: false, minWidth: 100, maxWidth: 600 },
        { key: 'shipmentCount', label: '出荷個数', sortable: false, minWidth: 90, maxWidth: 100 },
        { key: 'arrivalCount', label: '入荷個数', sortable: false, minWidth: 90, maxWidth: 100 },
        { key: 'isComplete', label: '完了', sortable: false, minWidth: 60, maxWidth: 80 },
      ],
      actions: [
        {
          label: '×',
          buttonClass: 'btn-danger',
          action: (item) => this.deleteProduct(item),
          disabled: (item) => !item.isComplete || this.isDetailMode
        }
      ],
      // カテゴリオプション
      categoryOptions: [],
      // メーカーオプション
      manufacturerOptions: [],
      // カテゴリの元データ（ランク連動用）
      originalCategories: [],
    };
  },
  async mounted() {
    const navigationFlag = sessionStorage.getItem('legitimateNavigation');
      if (navigationFlag !== 'StoreTransferArrival') {
        this.$router.push({ name: 'StoreTransferList' });
        return;
      }
      sessionStorage.removeItem('legitimateNavigation');
    
    // 詳細モードかどうかを判定
    this.isDetailMode = this.$route.query.mode === 'detail';
    
    // 初期データ取得
    await this.getInitData();
    
    this.loadStoreTransferData();
  },
  computed: {
    // 移動金額の計算（買取価格×在庫数の合計）
    calculateTotalAmount() {
      if (!this.allProducts || this.allProducts.length === 0) {
        return '¥0';
      }
      
      const total = this.allProducts.reduce((sum, product) => {
        const purchasePrice = product.purchasePrice || 0;
        const quantity = product.arrivalCount || 0;
        return sum + (purchasePrice * quantity);
      }, 0);
      
      return `¥${total.toLocaleString()}`;
    }
  },
  watch: {
    'productCode'(newVal) {
      if (newVal && newVal.length === constants.PRODUCT_CODE_LENGTH) {
        this.searchProduct();
      }
    }
  },
  methods: {
    // 初期データ取得処理
    async getInitData() {
      showLoading();
      try {
        // 店舗一覧を取得
        const storeResponse = await service.getStoreList();
        this.storeOptions = storeResponse.data.stores.map(store => ({
          value: store.id,
          label: store.storeName
        }));
        
        // API呼び出しでメーカー一覧を取得
        const makerResponse = await service.getMakerList();
        this.manufacturerOptions = makerResponse.data.makers.map(maker => {
          return { value: maker.id.toString(), label: maker.maker };
        });
        
        // API呼び出しでカテゴリ一覧を取得
        const categoryResponse = await service.getCategoryList();
        this.originalCategories = categoryResponse.data.categories;
        // カテゴリオプションを整形
        this.categoryOptions = [];
        for (const parent of this.originalCategories) {
          for (const child of parent.childCategories) {
            this.categoryOptions.push({
              value: child.id.toString(),
              label: `${parent.categoryType} - ${child.category}`
            });
          }
        }
      } catch (error) {
        console.error('初期情報の取得に失敗しました:', error);
        alert('初期情報の取得中にエラーが発生しました。');
      } finally {
        hideLoading();
      }
    },
    
    // 店舗名を取得
    getStoreName(storeId) {
      const store = this.storeOptions.find(store => store.value === storeId);
      return store ? store.label : '';
    },
    
    // 店舗移動データを取得
    async loadStoreTransferData() {
      try {
        showLoading();
        // URLパラメータから店舗移動IDを取得
        const storeTransferId = this.$route.query.id;
        if (!storeTransferId) {
          console.error('店舗移動IDが指定されていません');
          this.$router.push({ name: 'StoreTransferList' });
          return;
        }
        
        // 店舗移動データを取得
        const response = await service.getStoreTransferArrivalDetails(storeTransferId);
        
        if (!response.data.success) {
          alert('店舗移動データの取得に失敗しました');
          return;
        }
        
        this.storeTransfer = response.data.storeTransfer;
        this.allProducts = this.formatProductsForDisplay(response.data.products);
        this.totalCount = this.allProducts.length;
        
        // 現在のページに表示する商品を設定
        this.updateCurrentPageProducts();
      } catch (error) {
        console.error('店舗移動データの取得中にエラーが発生しました:', error);
        alert('店舗移動データの取得中にエラーが発生しました。');
      } finally {
        hideLoading();
      }
    },
    
    // 商品を検索
    async searchProduct() {
      if (!this.productCode) {
        alert('商品コードを入力してください');
        return;
      }
      
      try {
        showLoading();
        
        // 商品コードからデータを検索
        const storeTransferId = this.$route.query.id;
        const response = await service.getStoreTransferProductByCode(storeTransferId, this.productCode);
        
        if (response.data.success && response.data.product) {
          const product = response.data.product;
          
          // 既に追加済みの商品かチェック
          const existingProductIndex = this.allProducts.findIndex(p => p.id === product.id);
          
          if (existingProductIndex === -1) {
            // 商品が見つからない場合は警告
            alert('この商品は店舗移動対象ではありません');
            this.searchedProduct = null;
            this.productCode = '';
            return;
          }

          // 既存の商品がある場合は、出荷個数に入荷個数をセットして完了マークを付ける
          this.allProducts[existingProductIndex].arrivalCount = this.allProducts[existingProductIndex].shipmentCount;
          this.allProducts[existingProductIndex].isComplete = true;
          
          // 検索された商品情報を設定
          const formattedProduct = { ...this.allProducts[existingProductIndex] };
          this.searchedProduct = formattedProduct;
          
          // 入力フィールドをクリア
          this.productCode = '';

          // 現在のページに表示する商品を設定
          this.updateCurrentPageProducts();
        } else {
          alert('商品が見つかりませんでした');
        }
      } catch (error) {
        console.error('商品検索エラー:', error);
        alert('商品検索中にエラーが発生しました');
      } finally {
        hideLoading();
      }
    },
    // 現在のページに表示する商品を更新
    updateCurrentPageProducts() {
      const startIndex = this.currentPage * this.perPage;
      const endIndex = startIndex + this.perPage;
      this.displayProductList = this.allProducts.slice(startIndex, endIndex);
    },
    // 表示用にデータを整形するメソッド
    formatProductsForDisplay(products) {
      return products.map(product => {
        // 商品データを整形
        const formattedProduct = { ...product };
        
        // カテゴリIDをカテゴリ名に変換
        if (formattedProduct.categoryId) {
          formattedProduct.categoryName = this.getCategoryNameById(formattedProduct.categoryId);
        }
        
        // メーカーIDをメーカー名に変換
        if (formattedProduct.manufacturer) {
          formattedProduct.makerName = this.getMakerNameById(formattedProduct.manufacturer);
        }
        
        // 到着個数が未設定の場合は0を設定
        if (formattedProduct.arrivalCount === undefined) {
          formattedProduct.arrivalCount = 0;
        }
        
        // 完了フラグを設定
        formattedProduct.isComplete = formattedProduct.arrivalCount === formattedProduct.shipmentCount;
        
        return formattedProduct;
      });
    },
    
    // カテゴリIDからカテゴリ名を取得
    getCategoryNameById(categoryId) {
      const category = this.categoryOptions.find(option => option.value === categoryId.toString());
      return category ? category.label : categoryId;
    },
    
    // メーカーIDからメーカー名を取得
    getMakerNameById(makerId) {
      const maker = this.manufacturerOptions.find(option => option.value === makerId.toString());
      return maker ? maker.label : makerId;
    },
    
    // ページ変更ハンドラ
    onPageChange(page) {
      // ページ変更時にローカルページングを行う
      // FormListTableは1-indexedなので、0-indexedに変換する
      this.currentPage = page - 1;
      this.updateCurrentPageProducts();
    },
    
    // テーブルアクションのハンドラ
    handleTableAction(actionData) {
      const { action, item } = actionData;
      if (action === '削除') {
        this.deleteProduct(item);
      }
    },
    
    // 商品を削除
    deleteProduct(product) {
      const index = this.allProducts.findIndex(item => item.id === product.id);
      if (index === -1) return;
      // 商品を削除する代わりに、入荷個数を0にリセットして完了マークを外す
      this.allProducts[index].arrivalCount = 0;
      this.allProducts[index].isComplete = false;

      // 現在のページに表示する商品を設定
      this.updateCurrentPageProducts();
    },
    
    // 入荷登録を保存
    async saveArrival() {
      if (this.displayProductList.length === 0) {
        alert('商品が登録されていません');
        return;
      }
      
      try {
        showLoading();
        
        if (!confirm('入荷登録を行います。よろしいですか？')) {
          hideLoading();
          return;
        }
        
        // 店舗移動IDを取得
        const storeTransferId = this.$route.query.id;
        
        // リクエストデータを作成
        const requestData = {
          storeTransferId: parseInt(storeTransferId),
          products: this.allProducts.map(product => ({
            productId: product.id,
            arrivalCount: product.arrivalCount
          }))
        };
        
        // APIを呼び出して入荷登録
        const response = await service.registerArrivalTransfer(requestData);
        if (response.data && response.data.isSuccess) {
          alert('入荷登録が完了しました。');
          // 店舗移動一覧画面に遷移
          this.$router.push({ name: 'StoreTransferList' });
        } else {
          throw new Error(response.data?.message || '入荷情報の登録中にエラーが発生しました。');
        }
      } catch (error) {
        console.error('入荷登録エラー:', error);
        alert('入荷情報の登録中にエラーが発生しました。');
      } finally {
        hideLoading();
      }
    }
  }
}
</script>

<style scoped>
.status-complete {
  color: var(--green);
  font-weight: bold;
}
.status-incomplete {
  color: var(--gray);
}
.footer-buttons {
  display: flex;
  justify-content: flex-end;
  width: 100%;
  padding: 10px 0;
}

</style>
